import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { isObservable, Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';

import { ClaimValue } from 'src/app/infrastructure/models/claim-value';
import { ConfigService } from 'src/app/infrastructure/services/config-services/config.service';
import { ILoginDto } from 'src/assets/common/models/login-dto.g';
import { IRegisterDto } from 'src/assets/common/models/register-dto.g';
import { IForgotPasswordDto } from 'src/assets/common/models/forgot-password-dto.g';
import { IResetPasswordDto } from 'src/assets/common/models/reset-password-dto.g';
import { IChangePasswordDto } from 'src/assets/common/models/change-password-dto.g';
import { IChangeEmailDto } from 'src/assets/common/models/change-email-dto.g';
import { IConfirmEmailChangeDto } from 'src/assets/common/models/confirm-email-change-dto.g';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';

// Naam van het cross-tab BroadcastChannel-kanaal (zie __authChannel hieronder).
const AUTH_BROADCAST_CHANNEL = 'reflecs-auth-changed';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private readonly http = injectRequired(HttpClient);
    private readonly __configService = injectRequired(ConfigService);
    private readonly __router = injectRequired(Router);

    readonly ADMIN_PATH = '/admin';
    readonly INITIAL_PATH = '/home';
    readonly LOGIN_PATH = '/login';

    get API_URL() { return `${this.__configService.rootApi$$()}` }
    get AUTH_URL() { return `${this.__configService.rootAuth$$()}` }
    get LOGIN_URL() { return `${this.AUTH_URL}/login` }
    get VERIFYTWOFACTOR_URL() { return `${this.AUTH_URL}/verify-two-factor` }
    get REGISTER_URL() { return `${this.AUTH_URL}/register` }
    get CONFIRM_URL() { return `${this.AUTH_URL}/confirm` }
    get RESENDCONFIRM_URL() { return `${this.AUTH_URL}/resend-confirm` }
    get FORGOTPASSWORD_URL() { return `${this.AUTH_URL}/forgot-password` }
    get RESETPASSWORD_URL() { return `${this.AUTH_URL}/reset-password` }
    get CHANGEPASSWORD_URL() { return `${this.AUTH_URL}/change-password` }
    get CHANGEEMAIL_URL() { return `${this.AUTH_URL}/change-email` }
    get CONFIRMEMAILCHANGE_URL() { return `${this.AUTH_URL}/confirm-email-change` }
    get REFRESHTOKEN_URL() { return `${this.AUTH_URL}/refresh-token` }
    get REVOKETOKEN_URL() { return `${this.AUTH_URL}/revoke-token` }
    get TWOFACTOR_URL() { return `${this.API_URL}/TwoFactor` }

    // _token$ = new BehaviorSubject<string>('');
    token$$ = signal<string | null>(null);
    token$ = isObservable(this.token$$);
    get Token() { return this.token$$(); }
    set Token(value) { this.token$$.set(value); }

    private refreshTokenTimeout?: number;
    private __refreshInFlight$?: Observable<any>;
    get userData() {
        return this.getUserData$();
    }

    // Reactieve rollen uit het JWT (claim-key kan 'role'/'roles' of de MS-role-URI zijn).
    readonly Roles$$ = computed<string[]>(() => this.__rolesFromToken(this.token$$()));
    readonly IsAdmin$$ = computed<boolean>(() => this.Roles$$().includes('administrator'));

    private __rolesFromToken(token: string | null): string[] {
        if (!token) return [];
        try {
            const __payload = JSON.parse(window.atob(token.split('.')[1]));
            const __raw = __payload['role']
                ?? __payload['roles']
                ?? __payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
            if (!__raw) return [];
            return Array.isArray(__raw) ? __raw : [__raw];
        } catch {
            return [];
        }
    }

    // Cross-tab: alle open tabbladen delen dezelfde refresh-token-cookie (per-origin, niet per-tab),
    // maar het JWT zelf leeft bewust alleen in het geheugen van elke tab (geen localStorage — dat zou
    // het kwetsbaarder maken voor XSS-tokendiefstal). Zonder dit kanaal merkt een ander tabblad een
    // logout/login elders dus pas (indirect, via zijn EIGEN refresh-timer, tot de volle
    // token-levensduur later) — tot die tijd blijft het gewoon doorwerken als de oude gebruiker, en
    // neemt daarna zelfs stilzwijgend de NIEUWE sessie over via diezelfde gedeelde cookie (geen
    // hernieuwd inlogscherm). Het broadcast-bericht draagt bewust GEEN token mee (blijft
    // in-memory-only); elk ander tabblad reageert altijd met een geforceerde lokale logout +
    // navigatie naar /login — nooit door de nieuwe sessie stilzwijgend over te nemen, anders
    // verplaatst dat hetzelfde "ongemerkt van identiteit wisselen"-probleem alleen maar.
    private readonly __authChannel: BroadcastChannel | null =
        typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_BROADCAST_CHANNEL) : null;

    constructor() {
        if (this.__authChannel) {
            this.__authChannel.onmessage = () => this.__authChanged_elsewhere();
        }
    }

    // Alleen aanroepen vanuit een ECHTE, gebruiker-geïnitieerde login/logout (requestToken$,
    // postVerifyTwoFactor$, revokeToken$) — NIET vanuit updateToken()/revokeToken() zelf, want die
    // worden ook intern gebruikt door de stille achtergrond-refresh (elke ~token-levensduur) resp.
    // een mislukte refresh-cleanup; dat zijn geen cross-tab-relevante gebeurtenissen en zouden anders
    // ELK tabblad onnodig (of zelfs foutief, bij een mislukte refresh van een verder gezonde sessie)
    // laten uitloggen.
    private __authChanged_broadcast(): void {
        this.__authChannel?.postMessage(Date.now());
    }

    // Reactie in een ANDER tabblad op een login/logout elders: nooit stilzwijgend de (via de gedeelde
    // cookie inmiddels andere) sessie overnemen — altijd de eigen sessie hier ook opruimen en naar
    // /login sturen, zodat de gebruiker in dit tabblad bewust opnieuw moet inloggen. `revokeToken()`
    // is de lokale (niet-serverbellende) cleanup — de server-kant is al afgehandeld door het tabblad
    // dat de wijziging veroorzaakte. Navigatie is hier bewust EXPLICIET: appGuard checkt het token
    // alleen bij een nieuwe navigatiepoging, niet reactief op de al-geopende pagina, dus zonder deze
    // navigatie zou een leeggemaakt token de gebruiker gewoon op de huidige (mogelijk gevoelige)
    // pagina laten zitten.
    private __authChanged_elsewhere(): void {
        if (!this.Token) return;

        this.revokeToken();
        void this.__router.navigateByUrl(this.LOGIN_PATH);
    }

    getInitialPathForRole(roles?: string[]): string {
        return roles?.includes('administrator' as ClaimValue) ? this.ADMIN_PATH : this.INITIAL_PATH;
    }

    get$(url: string, params: HttpParams): Observable<any> {
        return this.http.get<any>(`${url}`, { params })
            .pipe(
                shareReplay(1)
            );
    }

    post$(url: string, data: any, params: HttpParams | null = new HttpParams(), options?: any): Observable<any> {
        const httpOptions = {
            params,
            ...(options?.observe && { observe: options.observe }),
            ...(options?.reportProgress !== undefined && { reportProgress: options.reportProgress }),
            ...(options?.withCredentials !== undefined && { withCredentials: options.withCredentials })
        };

        return this.http.post<any>(`${url}`, data, httpOptions);
    }

    patch$(url: string, data: any, params = new HttpParams(), options?: any): Observable<any> {
        const httpOptions = {
            params,
            ...(options?.observe && { observe: options.observe }),
            ...(options?.reportProgress !== undefined && { reportProgress: options.reportProgress }),
            ...(options?.withCredentials !== undefined && { withCredentials: options.withCredentials })
        };
          return this.http.patch<any>(`${url}`, data, httpOptions);
    }

    delete$(url: string, params = new HttpParams()): Observable<any> {
        return this.http.delete<any>(`${url}`, { params });
    }

    isAuthorized(): boolean {
        const _token = this.Token;
        
        return !!_token && _token.length > 0;
    }

    requestToken$(data: ILoginDto): Observable<any> {
        const params = new HttpParams();

        return this.post$(this.LOGIN_URL, data, params, { withCredentials: true } )
            .pipe(
                shareReplay(1),
                map(resp => {
                    const jwtToken = resp?.token;
                    if (!jwtToken) {
                        // 2FA-ingeschakeld account: terecht nog geen token, de tweede stap
                        // (postVerifyTwoFactor$) levert die pas na een geldige TOTP-code.
                        if (resp?.requiresTwoFactor) {
                            return resp;
                        }
                        throw Error('MissingToken');
                    }

                    this.updateToken(jwtToken);
                    this.__authChanged_broadcast();

                    return resp;
                })
            );
    }

    postVerifyTwoFactor$(pendingToken: string, code: string): Observable<any> {
        return this.post$(this.VERIFYTWOFACTOR_URL, { pendingToken, code }, new HttpParams(), { withCredentials: true })
            .pipe(
                shareReplay(1),
                map(resp => {
                    const jwtToken = resp?.token;
                    if (!jwtToken) {
                        throw Error('MissingToken');
                    }

                    this.updateToken(jwtToken);
                    this.__authChanged_broadcast();

                    return resp;
                })
            );
    }

    postRegister$(data: IRegisterDto): Observable<any> {
        return this.post$(this.REGISTER_URL, data);
    }

    // Body {email, code} is vereist door het BE-confirm-endpoint; de oude no-body-variant
    // was een dood pad (endpoint bestond backend-side niet).
    postConfirm$(email: string, code: string): Observable<any> {
        return this.post$(this.CONFIRM_URL, { email, code });
    }

    postResendConfirm$(email: string): Observable<any> {
        return this.post$(this.RESENDCONFIRM_URL, { email });
    }

    postForgotPassword$(email: string): Observable<any> {
        const data: IForgotPasswordDto = { email };
        return this.post$(this.FORGOTPASSWORD_URL, data);
    }

    postResetPassword$(email: string, code: string, password: string): Observable<any> {
        const data: IResetPasswordDto = { email, code, password };
        return this.post$(this.RESETPASSWORD_URL, data);
    }

    postChangePassword$(currentPassword: string, newPassword: string): Observable<any> {
        const data: IChangePasswordDto = { currentPassword, newPassword };
        return this.post$(this.CHANGEPASSWORD_URL, data);
    }

    postChangeEmail$(newEmail: string): Observable<any> {
        const data: IChangeEmailDto = { newEmail };
        return this.post$(this.CHANGEEMAIL_URL, data);
    }

    postConfirmEmailChange$(email: string, newEmail: string, code: string): Observable<any> {
        const data: IConfirmEmailChangeDto = { email, newEmail, code };
        return this.post$(this.CONFIRMEMAILCHANGE_URL, data);
    }

    getTwoFactorStatus$(): Observable<any> {
        return this.get$(`${this.TWOFACTOR_URL}/status`, new HttpParams());
    }

    // reset = nieuwe authenticator-key (bv. telefoon kwijt); standaard blijft de bestaande key en
    // daarmee het account in de authenticator-app geldig.
    postTwoFactorEnroll$(reset: boolean = false): Observable<any> {
        return this.post$(`${this.TWOFACTOR_URL}/enroll`, {}, new HttpParams().set('reset', reset));
    }

    postTwoFactorConfirm$(code: string): Observable<any> {
        return this.post$(`${this.TWOFACTOR_URL}/confirm`, { code });
    }

    postTwoFactorDisable$(password: string): Observable<any> {
        return this.post$(`${this.TWOFACTOR_URL}/disable`, { password });
    }

    refreshToken$(): Observable<any> {
        // Single-flight: gelijktijdige aanroepen (app-initializer + timer, of meerdere wachtende
        // requests) delen één lopende HTTP-refresh. Zonder dit roteren twee parallelle requests
        // dezelfde cookie-refresh-token; de eerste bumpt de rowversion en de tweede faalt server-
        // side met een DbUpdateConcurrencyException (UPDATE op 0 rijen van het RefreshToken-record).
        if (this.__refreshInFlight$) return this.__refreshInFlight$;

        this.__refreshInFlight$ = this.post$(`${this.REFRESHTOKEN_URL}`, null, null, { withCredentials: true } )
            .pipe(
                tap(resp => {this.updateToken(resp?.token)}),
                catchError(err => {
                    this.revokeToken();

                    return of(err);
                }),
                finalize(() => { this.__refreshInFlight$ = undefined; }),
                shareReplay(1),
            );

        return this.__refreshInFlight$;
    }

    revokeToken$(): Observable<any> {
        return this.post$(`${this.REVOKETOKEN_URL}`, null, null, { withCredentials: true } )
            .pipe(
                tap((resp) => {
                    this.revokeToken();
                    this.__authChanged_broadcast();
                })
            );
    }

    getUserData$(): Observable<any> {
        const token = this.Token;
        if (token) {
            const encodedPayload = token.split('.')[1];
            const payload = window.atob(encodedPayload); // Decode a base-64 encoded string
            return of(JSON.parse(payload));
        } else {
            return of(undefined);
        }
    }

    updateToken(jwtToken: string) {
        this.Token = jwtToken;
        this.startRefreshTokenTimer();
    }

    revokeToken() {
        this.stopRefreshTokenTimer();
        this.Token = null;
    }

    startRefreshTokenTimer() {
        // Ruim een eventueel lopende timer eerst op: zonder dit stapelen setTimeout's zich op
        // (updateToken roept dit na elke refresh/login aan), waardoor er na verloop van tijd
        // meerdere refreshes vrijwel gelijktijdig vuren — precies de race die de server-side
        // concurrency-fout veroorzaakt.
        this.stopRefreshTokenTimer();

        // parse json object from base64 encoded jwt token
        if (!this.Token) {
            return;
        }

        const token = window.atob(this.Token.split('.')[1]);
        const jwtToken = JSON.parse(token);
        // set a timeout to refresh the token a minute before it expires
        const expires = new Date(jwtToken.exp * 1000);
        const timeout = expires.getTime() - Date.now() - (60 * 1000);
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken$().subscribe(), timeout);
    }

    stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }
}
